/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import { Button } from '@styles/button.style'
import useRootStore from '@store/useRootStore'
import { GAME_TYPE } from '@game-type/game-market-types'
import React, { Fragment } from 'react'
import _ from 'lodash'

import { useOnMount, useOnUnmount } from '@utils/reactExt'
// css
import { LS_SPORT_ID_ICON_MAP, SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import { MatchTeamInfoWrap, MatchWrapper, SportsIco } from '@styles/match.style'
import LeagueEmblemImg from '@assets/images/icon/emblem_icon/league.png'
import { TxtLimit } from '@styles/base.style'
import { EMPTY_ICON, MATCHUP_DETAIL_CONTENT_TAB_KEY } from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'
import { EXCEPTIONAL_UNIQUE_ID } from '@v2/constants/common'
import { Schema$MatchTrack, Schema$TournamentTrack } from '@protocol/game'
import { DetailMatchTrack } from '@store/matchupStore'
import { isLsportsEnv } from '@utils/utils'
import { ChangeMatchWrap } from './index.style'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import dayjs from 'dayjs'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import { ListView } from 'antd-mobile'
import BottomLoading from '@components/BottomLoading'

dayjs.extend(dayOfYear)

export interface ChangeMatchProps {
  gameType: GAME_TYPE
}

const ChangeMatchPopup: React.FC<ChangeMatchProps> = (props: ChangeMatchProps) => {
  const { gameType } = props
  const { matchupStore, globalStore } = useRootStore()
  const {
    matchChangeTournamentTracksStartDate,
    matchChangeTournamentTracks,
    detailTrack,
    matchChangeHasMore,
    isTournamentTrackLoading,
    matchChangeLastOrderId,
  } = matchupStore

  if (!detailTrack) {
    return (<Fragment/>)
  }

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1, row2) => {
      return row1 !== row2
    },
  })

  const currentMatchId = detailTrack.track.matchId

  const fetchParams = () => ({
    clubId: matchupStore.club.id,
    gameType: matchupStore.currentGameKey,
    sportIdGroup: matchupStore.currentSportId,
    sportId: matchupStore.currentSportId,
    uniqueId: matchupStore.detailTrack.tournamentId,
    uniqueIdGroup: EXCEPTIONAL_UNIQUE_ID.PROGRESSING_MATCH,
    tournamentIds: [matchupStore.detailTrack.tournamentId],
    onlyIrregularCount: true,
    ignoreMatchupFilterList: true,
  })

  const fetchParamsWithOrderId = (orderId: string) => ({
    ...fetchParams(),
    orderId,
  })

  useOnMount(() => {
    const params = fetchParams()
    matchupStore.fetchMatchChangeTournamentTracks(params).catch(error => console.error(error))
  })

  const fetchData = async () => {
    const params = fetchParamsWithOrderId(matchChangeLastOrderId)
    await matchupStore.fetchMatchChangeTournamentTracks(params).catch(error => console.error(error))
  }

  useOnUnmount(() => {
    matchupStore.resetMatchChangeTournamentTracks()
    matchupStore.resetMatchChangeTournamentTracksStartDate()
  })

  const sortMatches = matches => {
    matches.sort((a, b) => {
      let result = dayjs(a.dateOfMatch).toDate().getTime() - dayjs(b.dateOfMatch).toDate().getTime()
      if (result === 0) {
        result = a.matchId - b.matchId
      }
      return result
    })
  }

  const dateTextTemplate = date => {
    return dayjs(date).format('MM/DD(ddd)')
  }

  const timeTextTemplate = date => {
    return dayjs(date).format('HH:mm')
  }

  const scoreTemplate = score => _.isNaN(score) ? '-' : score

  const rowDateTextTemplate = date => {
    let text: string
    const baseDayOfYear = dayjs(matchChangeTournamentTracksStartDate).dayOfYear()
    const dayOfYear = dayjs(date).dayOfYear()
    if (baseDayOfYear === dayOfYear) {
      text = '오늘'
    } else if (baseDayOfYear + 1 === dayOfYear) {
      text = '내일'
    } else {
      text = dateTextTemplate(date)
    }
    return text
  }

  const makePreMatchHeader = (dateOfMatch: Date) => {
    return (
      <span className="tournament-date">{dateTextTemplate(dateOfMatch)}</span>
    )
  }

  const makeLiveMatchHeader = track => {
    return (
      <Fragment>
        <SportsIco className="sports-ico">
          {isLsportsEnv() ? LS_SPORT_ID_ICON_MAP[track.sportId] : SPORT_ID_ICON_MAP[track.sportId]}
        </SportsIco>
        <span className="country">{track.categoryName}</span>
        <span className="tournament-emblem">
          <img src={track.tournamentEmblem || LeagueEmblemImg} alt={`${track.tournamentName} 이미지`} className="league-ico"/>
        </span>
        <span className="tournament-name" css={TxtLimit}>
          {track.tournamentName}
        </span>
      </Fragment>
    )
  }

  const onClickRow = async (detailMatchTrack: DetailMatchTrack) => {
    const { producerId, club } = matchupStore
    const matchTrack = detailMatchTrack.track
    const { matchId } = matchTrack
    const isLive = gameType === GAME_TYPE.LIVE
    matchupStore.setDetailMatchId(matchId)
    // matchupStore.updateMatchupDetailMarkets(matchId, producerId, isLive, detailMatchTrack)
    matchupStore.setMatchupDetailContentTabKey(MATCHUP_DETAIL_CONTENT_TAB_KEY.BET)
    await matchupStore.fetchMatchupDetailMarkets({
      gameType: matchupStore.gameKey, clubId: club?.id, producerId, matchId,
      tournamentId: detailMatchTrack.tournamentId,
    }, isLive)
    globalStore.closeDialog()
  }

  const makeMatchRow = (detailMatchTrack: DetailMatchTrack) => {
    const matchTrack = detailMatchTrack.track
    return (
      <div key={matchTrack.matchId} className="change-match-list">
        <Button onClick={() => {
          onClickRow(detailMatchTrack)
        }} className="change-match-list--btn">

          <MatchTeamInfoWrap className="change-match-modal">
            <div className="game-info-team home">
              <img src={matchTrack.homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[detailMatchTrack.sportId]} alt="홈팀 엠블럼" className="emblem"/>,
              <span className="team-name">{matchTrack.homeTeamName}</span>
            </div>

            <div className="game-info-center">

              {gameType !== GAME_TYPE.LIVE ? (
                <Fragment>
                  {/* ************ 일반 경기 ************ */}
                  <span className="date">{rowDateTextTemplate(matchTrack.dateOfMatch)}</span>
                  <span className="time">{timeTextTemplate(matchTrack.dateOfMatch)}</span>
                </Fragment>
              ) : (
                <Fragment>
                  {/* ************ 진행중인 라이브 경기일 경우 ************ */}
                  <span className="score">{scoreTemplate(matchTrack.homeScore)} : {scoreTemplate(matchTrack.awayScore)}</span>
                  <span className="status">{matchTrack.matchStatusName}</span>
                </Fragment>
              )}

              {/* ************ 현재 보고 있는 경기 ************ */}
              {matchTrack.matchId === currentMatchId && <span className="current-match">현재매치</span>}
            </div>
            <div className="game-info-team away">
              <img src={matchTrack.awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[detailMatchTrack.sportId]} alt="원정팀 엠블럼" className="emblem"/>,
              <span className="team-name">{matchTrack.awayTeamName}</span>
            </div>
          </MatchTeamInfoWrap>

        </Button>
        <span className="line"/>
      </div>)
  }

  const makePreMatchGroupElements = () => {
    const elements = []
    _.forEach(matchChangeTournamentTracks, track => {
      sortMatches(track.matches)

      let header
      let rows = []
      let currentDayOfYear
      _.forEach(track.matches, match => {
        const dayOfYear = dayjs(match.dateOfMatch).dayOfYear()
        if (_.isNil(currentDayOfYear)) {
          currentDayOfYear = dayOfYear
          header = makePreMatchHeader(match.dateOfMatch)
        }

        if (currentDayOfYear !== dayOfYear) {
          elements.push({ header, rows })

          currentDayOfYear = dayOfYear
          header = makePreMatchHeader(match.dateOfMatch)
          rows = []
        }

        rows.push(makeMatchRow(makeDetailMatchTrack(track, match)))
      })
      if (header) {
        elements.push({ header, rows })
      }
    })
    return elements
  }

  const makeDetailMatchTrack = (
    tournamentTrack: Schema$TournamentTrack,
    matchTrack: Schema$MatchTrack,
  ): DetailMatchTrack => {
    return {
      gameKey: gameType,

      sportId: tournamentTrack.sportId,
      categoryId: tournamentTrack.categoryId,
      tournamentId: tournamentTrack.tournamentId,

      sportName: tournamentTrack.sportName,
      categoryName: tournamentTrack.categoryName,
      tournamentName: tournamentTrack.tournamentName,

      sportEmblem: tournamentTrack.sportEmblem,
      categoryEmblem: tournamentTrack.categoryEmblem,
      tournamentEmblem: tournamentTrack.tournamentEmblem,

      track: matchTrack,
    }
  }

  const sortLiveTournamentTracks = () => {
    matchChangeTournamentTracks.sort((a, b) => {
      let result = dayjs(a.firstDateOfMatch).toDate().getTime() - dayjs(b.firstDateOfMatch).toDate().getTime()
      if (result === 0) {
        result = a.sportId - b.sportId
      }
      if (result === 0) {
        result = a.tournamentId - b.tournamentId
      }
      return result
    })
  }

  const sortLiveMatches = () => {
    _.forEach(matchChangeTournamentTracks, track => {
      sortMatches(track.matches)
    })
  }

  const makeLiveMatchGroupElements = () => {
    sortLiveTournamentTracks()
    sortLiveMatches()

    const elements = []
    let header
    let rows
    _.forEach(matchChangeTournamentTracks, track => {
      rows = []
      header = makeLiveMatchHeader(track)
      _.forEach(track.matches, match => {
        rows.push(makeMatchRow(makeDetailMatchTrack(track, match)))
      })
      elements.push({ header, rows })
    })

    return elements
  }

  const renderMatchGroups = () => {
    const contents = []
    if (!matchChangeTournamentTracks) {
      return contents
    }

    let matchGroups
    if (gameType === GAME_TYPE.LIVE) {
      matchGroups = makeLiveMatchGroupElements()
    } else {
      matchGroups = makePreMatchGroupElements()
    }

    const matchGroupKeyBase = 'matchGroupKey:'
    let matchGroupKeyCount = 0
    _.forEach(matchGroups, matchGroup => {
      contents.push((<div key={`${matchGroupKeyBase}${matchGroupKeyCount += 1}`} className="change-match-group">
        <MatchWrapper className="change-match-modal">
          <div className="match-block-header">
            <div className="tournament-info">
              {matchGroup.header}
            </div>
          </div>
        </MatchWrapper>
        {matchGroup.rows.length >= 20 ?
          (
            <div style={{ height: '350px' }}>
              <ListView dataSource={dataSource.cloneWithRows(matchGroup.rows)}
                        renderRow={(row) => row}
                        css={{ height: '100%' }}
                        initialListSize={20}
                        pageSize={20}
                        onEndReachedThreshold={10}
                        onEndReached={endReachedHandler}
                        scrollRenderAheadDistance={500}
                        renderFooter={renderFooter}
              />
            </div>
          ) :
          (
            <div>
              {matchGroup.rows}
            </div>
          )
        }
      </div>))
    })
    return contents
  }

  const renderEmptyContent = () => {
    return (
      <AdvEmpty ico={gameType === GAME_TYPE.LIVE ? EMPTY_ICON.CHANGE_MATCH_LIVE : EMPTY_ICON.CHANGE_MATCH}/>
    )
  }

  let isEmpty = false
  if (matchChangeTournamentTracks && matchChangeTournamentTracks.length === 0) {
    isEmpty = true
  }
  if (matchChangeTournamentTracks && matchChangeTournamentTracks.length === 1) {
    const onlyCurrentMatch = _.find(matchChangeTournamentTracks, track => {
      const existsMatch = _.find(track.matches, match => {
        return match.matchId === detailTrack.track.matchId
      })
      return track.matches.length === 1 && existsMatch
    })
    if (onlyCurrentMatch) {
      isEmpty = true
    }
  }

  const endReachedHandler = async () => {
    if (matchChangeTournamentTracks) {
      if (matchChangeHasMore) {
        await fetchData()
      }
    }
  }

  const renderFooter = () => {
    let footer = null
    if (isTournamentTrackLoading) {
      footer = (<BottomLoading/>)
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  return (
    <Fragment>
      {isEmpty ?
        renderEmptyContent()
        :
        <ChangeMatchWrap>
          {renderMatchGroups()}
        </ChangeMatchWrap>
      }
    </Fragment>
  )
}

export default observer(ChangeMatchPopup)
